/**
 * Procedural noise toolkit. ARCHITECTURE.md §4 (`src/materials`), §6 (periodic noise where
 * tiling is required), §0.4 (no `Math.random()` — every value here is a pure function of its
 * integer seed and coordinates).
 *
 * This module imports nothing. It is pure arithmetic so `tests/unit` and `tools/` can use it
 * without three.js and without a DOM.
 *
 * ---------------------------------------------------------------------------
 * Two layers, and why
 * ---------------------------------------------------------------------------
 * **Layer 1 — lattice noise with an explicit period.** `valueNoise2(x, y, seed, px, py)` and
 * friends operate in arbitrary lattice space. Pass `px`/`py` > 0 and the lattice indices wrap,
 * which makes the field exactly periodic. Pass 0 for an unbounded field.
 *
 * **Layer 2 — unit-tile noise addressed by frequency.** `tile*` functions take `(u, v)` in
 * `[0,1)` texture space plus an **integer frequency** = "repeats across the tile". Period is
 * then implied by frequency, so *every* `tile*` result is seamless by construction — you cannot
 * accidentally bake a texture with a visible seam. All bakers in `bake.js` use layer 2.
 *
 * Domain warping preserves seamlessness: `f(u + p(u,v), v + q(u,v))` is 1-periodic whenever
 * `f`, `p` and `q` all are, which is why `tileWarp2` only ever offsets by a `tile*` field.
 *
 * Multi-value results (Worley distances, cell ids, warped coordinates) are written into a caller
 * supplied `out` record so the functions can be called from `update()` without allocating.
 */

// ---------------------------------------------------------------------------
// Small scalar helpers
// ---------------------------------------------------------------------------

/** @returns {number} `v` clamped to `[0,1]`. */
export function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** @returns {number} `v` clamped to `[lo,hi]`. */
export function clampTo(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Linear interpolation. */
export function mix(a, b, t) {
  return a + (b - a) * t;
}

/** Positive fractional part, correct for negatives. */
export function fract(v) {
  return v - Math.floor(v);
}

/** Hermite smoothstep between two edges. */
export function smoothstep(edge0, edge1, v) {
  if (edge1 === edge0) return v < edge0 ? 0 : 1;
  const t = clamp01((v - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** Quintic fade — C2 continuous, which is what keeps normal maps free of lattice creases. */
export function fade5(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Cubic fade — cheaper, C1 only. Fine for masks, not for height fields. */
export function fade3(t) {
  return t * t * (3 - 2 * t);
}

/** Maps `[-1,1]` to `[0,1]`. */
export function unsignedOf(v) {
  return v * 0.5 + 0.5;
}

/** Maps `[0,1]` to `[-1,1]`. */
export function signedOf(v) {
  return v * 2 - 1;
}

/**
 * Wraps a lattice index into `[0, period)`. `period <= 0` disables wrapping, which is how the
 * same code path serves both periodic (tiling) and unbounded fields.
 */
export function wrapi(i, period) {
  if (period <= 0) return i | 0;
  const p = period | 0;
  return (((i | 0) % p) + p) % p;
}

// ---------------------------------------------------------------------------
// Integer hashing — the deterministic substrate under everything else
// ---------------------------------------------------------------------------

const HX = 0x27d4eb2d;
const HY = 0x165667b1;
const HZ = 0x9e3779b1;
const HS = 0x85ebca6b;

/** 32-bit avalanche hash of a 2D integer lattice cell. */
export function ihash2(x, y, seed = 0) {
  let h = (Math.imul(x | 0, HX) ^ Math.imul(y | 0, HY) ^ Math.imul(seed | 0, HS)) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0x27d4eb2f) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/** 32-bit avalanche hash of a 3D integer lattice cell. */
export function ihash3(x, y, z, seed = 0) {
  let h =
    (Math.imul(x | 0, HX) ^ Math.imul(y | 0, HY) ^ Math.imul(z | 0, HZ) ^ Math.imul(seed | 0, HS)) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0x27d4eb2f) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/** Hash of one integer, useful for deriving sub-seeds from a layer index. */
export function ihash1(x, seed = 0) {
  return ihash2(x, 0x51ed, seed);
}

/** Uniform value in `[0,1)` for a 2D lattice cell. */
export function hash01(x, y, seed = 0) {
  return ihash2(x, y, seed) / 4294967296;
}

/** Uniform value in `[0,1)` for a 3D lattice cell. */
export function hash01_3(x, y, z, seed = 0) {
  return ihash3(x, y, z, seed) / 4294967296;
}

// 16 evenly spaced unit gradients. Precomputed once at module load; deterministic.
const GRAD2_COUNT = 16;
const GRAD2 = new Float32Array(GRAD2_COUNT * 2);
for (let i = 0; i < GRAD2_COUNT; i++) {
  const a = (i / GRAD2_COUNT) * Math.PI * 2;
  GRAD2[i * 2] = Math.cos(a);
  GRAD2[i * 2 + 1] = Math.sin(a);
}

// The 12 cube-edge-midpoint gradients used by classic gradient noise, normalised.
const INV_SQRT2 = 1 / Math.SQRT2;
const GRAD3 = new Float32Array([
  1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0,
  1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, -1,
  0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1,
]);
for (let i = 0; i < GRAD3.length; i++) GRAD3[i] *= INV_SQRT2;

// ---------------------------------------------------------------------------
// Layer 1 — value noise
// ---------------------------------------------------------------------------

/**
 * 2D value noise in `[0,1]`, quintic interpolation.
 * @param {number} periodX lattice period in x; 0 = unbounded
 * @param {number} periodY lattice period in y; 0 = unbounded
 */
export function valueNoise2(x, y, seed = 0, periodX = 0, periodY = 0) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const u = fade5(x - xi);
  const v = fade5(y - yi);
  const x0 = wrapi(xi, periodX);
  const x1 = wrapi(xi + 1, periodX);
  const y0 = wrapi(yi, periodY);
  const y1 = wrapi(yi + 1, periodY);
  const a = hash01(x0, y0, seed);
  const b = hash01(x1, y0, seed);
  const c = hash01(x0, y1, seed);
  const d = hash01(x1, y1, seed);
  return mix(mix(a, b, u), mix(c, d, u), v);
}

/** 3D value noise in `[0,1]`, quintic interpolation. */
export function valueNoise3(x, y, z, seed = 0, periodX = 0, periodY = 0, periodZ = 0) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  const u = fade5(x - xi);
  const v = fade5(y - yi);
  const w = fade5(z - zi);
  const x0 = wrapi(xi, periodX);
  const x1 = wrapi(xi + 1, periodX);
  const y0 = wrapi(yi, periodY);
  const y1 = wrapi(yi + 1, periodY);
  const z0 = wrapi(zi, periodZ);
  const z1 = wrapi(zi + 1, periodZ);
  const c000 = hash01_3(x0, y0, z0, seed);
  const c100 = hash01_3(x1, y0, z0, seed);
  const c010 = hash01_3(x0, y1, z0, seed);
  const c110 = hash01_3(x1, y1, z0, seed);
  const c001 = hash01_3(x0, y0, z1, seed);
  const c101 = hash01_3(x1, y0, z1, seed);
  const c011 = hash01_3(x0, y1, z1, seed);
  const c111 = hash01_3(x1, y1, z1, seed);
  const z0v = mix(mix(c000, c100, u), mix(c010, c110, u), v);
  const z1v = mix(mix(c001, c101, u), mix(c011, c111, u), v);
  return mix(z0v, z1v, w);
}

// ---------------------------------------------------------------------------
// Layer 1 — gradient noise
// ---------------------------------------------------------------------------

function grad2Dot(cx, cy, seed, dx, dy) {
  const g = (ihash2(cx, cy, seed) % GRAD2_COUNT) * 2;
  return GRAD2[g] * dx + GRAD2[g + 1] * dy;
}

/**
 * 2D gradient (Perlin-style) noise in roughly `[-1,1]`. Higher-quality low frequencies than
 * value noise — no axis-aligned blockiness — which is why base surface forms use it.
 */
export function gradientNoise2(x, y, seed = 0, periodX = 0, periodY = 0) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = fade5(xf);
  const v = fade5(yf);
  const x0 = wrapi(xi, periodX);
  const x1 = wrapi(xi + 1, periodX);
  const y0 = wrapi(yi, periodY);
  const y1 = wrapi(yi + 1, periodY);
  const n00 = grad2Dot(x0, y0, seed, xf, yf);
  const n10 = grad2Dot(x1, y0, seed, xf - 1, yf);
  const n01 = grad2Dot(x0, y1, seed, xf, yf - 1);
  const n11 = grad2Dot(x1, y1, seed, xf - 1, yf - 1);
  // Unit gradients cap the raw range at 1/sqrt(2); rescale so the result spans ~[-1,1].
  return mix(mix(n00, n10, u), mix(n01, n11, u), v) * Math.SQRT2;
}

function grad3Dot(cx, cy, cz, seed, dx, dy, dz) {
  const g = (ihash3(cx, cy, cz, seed) % 12) * 3;
  return GRAD3[g] * dx + GRAD3[g + 1] * dy + GRAD3[g + 2] * dz;
}

/** 3D gradient noise in roughly `[-1,1]`. Used by the triplanar helpers. */
export function gradientNoise3(x, y, z, seed = 0, periodX = 0, periodY = 0, periodZ = 0) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  const xf = x - xi;
  const yf = y - yi;
  const zf = z - zi;
  const u = fade5(xf);
  const v = fade5(yf);
  const w = fade5(zf);
  const x0 = wrapi(xi, periodX);
  const x1 = wrapi(xi + 1, periodX);
  const y0 = wrapi(yi, periodY);
  const y1 = wrapi(yi + 1, periodY);
  const z0 = wrapi(zi, periodZ);
  const z1 = wrapi(zi + 1, periodZ);
  const n000 = grad3Dot(x0, y0, z0, seed, xf, yf, zf);
  const n100 = grad3Dot(x1, y0, z0, seed, xf - 1, yf, zf);
  const n010 = grad3Dot(x0, y1, z0, seed, xf, yf - 1, zf);
  const n110 = grad3Dot(x1, y1, z0, seed, xf - 1, yf - 1, zf);
  const n001 = grad3Dot(x0, y0, z1, seed, xf, yf, zf - 1);
  const n101 = grad3Dot(x1, y0, z1, seed, xf - 1, yf, zf - 1);
  const n011 = grad3Dot(x0, y1, z1, seed, xf, yf - 1, zf - 1);
  const n111 = grad3Dot(x1, y1, z1, seed, xf - 1, yf - 1, zf - 1);
  const a = mix(mix(n000, n100, u), mix(n010, n110, u), v);
  const b = mix(mix(n001, n101, u), mix(n011, n111, u), v);
  return mix(a, b, w) * 1.4142136;
}

/**
 * Flow noise: gradient noise whose gradients rotate with `phase`. Advancing `phase` makes the
 * field swirl in place instead of sliding, which is what sells drifting steam and rain sheets.
 * `phase` is in turns, so `phase = simSeconds * rate`.
 */
export function flowNoise2(x, y, phase, seed = 0, periodX = 0, periodY = 0) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = fade5(xf);
  const v = fade5(yf);
  const x0 = wrapi(xi, periodX);
  const x1 = wrapi(xi + 1, periodX);
  const y0 = wrapi(yi, periodY);
  const y1 = wrapi(yi + 1, periodY);
  const rot = phase * Math.PI * 2;
  const n00 = rotGradDot(x0, y0, seed, xf, yf, rot);
  const n10 = rotGradDot(x1, y0, seed, xf - 1, yf, rot);
  const n01 = rotGradDot(x0, y1, seed, xf, yf - 1, rot);
  const n11 = rotGradDot(x1, y1, seed, xf - 1, yf - 1, rot);
  return mix(mix(n00, n10, u), mix(n01, n11, u), v) * Math.SQRT2;
}

function rotGradDot(cx, cy, seed, dx, dy, rot) {
  const h = ihash2(cx, cy, seed);
  const base = (h / 4294967296) * Math.PI * 2 + rot;
  return Math.cos(base) * dx + Math.sin(base) * dy;
}

// ---------------------------------------------------------------------------
// Layer 1 — Worley / cellular
// ---------------------------------------------------------------------------

/** Result record for {@link worley2}. Create one per call site and reuse it. */
export function createCellResult() {
  return { f1: 0, f2: 0, cellX: 0, cellY: 0, cellId: 0, cellValue: 0, dx: 0, dy: 0 };
}

/**
 * Periodic Worley/cellular noise. Writes F1, F2, the winning cell's wrapped indices, its 32-bit
 * id and a `[0,1)` value derived from that id, plus the offset to the winning feature point.
 *
 * Feature points are jittered within `[0.075, 0.925]` of their cell rather than the full cell.
 * A 3x3 neighbourhood search is only *provably* correct for smaller jitter, and clamping this
 * way keeps F1/F2 exact for the cell sizes we bake while staying visually irregular.
 *
 * @param {number} period cell period; 0 = unbounded
 * @param {object} out record from {@link createCellResult}
 */
export function worley2(x, y, seed = 0, period = 0, out = createCellResult()) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  let f1 = 1e30;
  let f2 = 1e30;
  let bestId = 0;
  let bestX = 0;
  let bestY = 0;
  let bestDx = 0;
  let bestDy = 0;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const gx = xi + dx;
      const gy = yi + dy;
      const wx = wrapi(gx, period);
      const wy = wrapi(gy, period);
      const h = ihash2(wx, wy, seed);
      const jx = 0.075 + ((h & 0xffff) / 65536) * 0.85;
      const jy = 0.075 + (((h >>> 16) & 0xffff) / 65536) * 0.85;
      const ox = gx + jx - x;
      const oy = gy + jy - y;
      const d = Math.sqrt(ox * ox + oy * oy);
      if (d < f1) {
        f2 = f1;
        f1 = d;
        bestId = h;
        bestX = wx;
        bestY = wy;
        bestDx = ox;
        bestDy = oy;
      } else if (d < f2) {
        f2 = d;
      }
    }
  }
  out.f1 = f1;
  out.f2 = f2;
  out.cellX = bestX;
  out.cellY = bestY;
  out.cellId = bestId;
  out.cellValue = bestId / 4294967296;
  out.dx = bestDx;
  out.dy = bestDy;
  return out;
}

// Module-scope scratch so the convenience wrappers stay allocation free.
const CELL_SCRATCH = createCellResult();

/** Distance to the nearest feature point, clamped to `[0,1]`. */
export function worleyF1(x, y, seed = 0, period = 0) {
  worley2(x, y, seed, period, CELL_SCRATCH);
  return clamp01(CELL_SCRATCH.f1);
}

/** Distance to the second-nearest feature point, clamped to `[0,1]`. */
export function worleyF2(x, y, seed = 0, period = 0) {
  worley2(x, y, seed, period, CELL_SCRATCH);
  return clamp01(CELL_SCRATCH.f2);
}

/** `F2 - F1`: near zero on cell boundaries. The basis of every crack pattern here. */
export function worleyF2F1(x, y, seed = 0, period = 0) {
  worley2(x, y, seed, period, CELL_SCRATCH);
  return clamp01(CELL_SCRATCH.f2 - CELL_SCRATCH.f1);
}

/** 32-bit id of the containing Voronoi cell — flat cell colouring, per-cell wear, per-tile decay. */
export function voronoiCellId(x, y, seed = 0, period = 0) {
  worley2(x, y, seed, period, CELL_SCRATCH);
  return CELL_SCRATCH.cellId >>> 0;
}

/** `[0,1)` value that is constant inside each Voronoi cell. */
export function voronoiCellValue(x, y, seed = 0, period = 0) {
  worley2(x, y, seed, period, CELL_SCRATCH);
  return CELL_SCRATCH.cellValue;
}

// ---------------------------------------------------------------------------
// Layer 2 — unit-tile noise, addressed by integer frequency (always seamless)
// ---------------------------------------------------------------------------

function freqOf(f) {
  const n = Math.round(f);
  return n < 1 ? 1 : n;
}

/** Seamless value noise over the unit tile. `freqX`/`freqY` = repeats across the tile. */
export function tileValue2(u, v, freqX, freqY, seed = 0) {
  const fx = freqOf(freqX);
  const fy = freqOf(freqY);
  return valueNoise2(u * fx, v * fy, seed, fx, fy);
}

/** Seamless gradient noise over the unit tile, in `[-1,1]`. */
export function tileGradient2(u, v, freqX, freqY, seed = 0) {
  const fx = freqOf(freqX);
  const fy = freqOf(freqY);
  return gradientNoise2(u * fx, v * fy, seed, fx, fy);
}

/** Seamless flow noise over the unit tile, in `[-1,1]`. `phase` in turns. */
export function tileFlow2(u, v, freqX, freqY, phase, seed = 0) {
  const fx = freqOf(freqX);
  const fy = freqOf(freqY);
  return flowNoise2(u * fx, v * fy, phase, seed, fx, fy);
}

/**
 * Seamless fractal Brownian motion over the unit tile, in `[0,1]`.
 * Octave frequencies are `freq * lacunarity^o`, all integers, so every octave tiles.
 *
 * @param {object} [opt]
 * @param {number} [opt.freqX] base repeats in u
 * @param {number} [opt.freqY] base repeats in v (defaults to freqX)
 * @param {number} [opt.octaves]
 * @param {number} [opt.gain] amplitude falloff per octave
 * @param {number} [opt.lacunarity] integer frequency multiplier per octave
 * @param {number} [opt.seed]
 * @param {boolean} [opt.gradient] use gradient noise instead of value noise
 */
export function tileFbm2(u, v, opt) {
  const freqX = freqOf(opt.freqX !== undefined ? opt.freqX : 4);
  const freqY = freqOf(opt.freqY !== undefined ? opt.freqY : freqX);
  const octaves = opt.octaves !== undefined ? opt.octaves : 4;
  const gain = opt.gain !== undefined ? opt.gain : 0.5;
  const lac = Math.max(2, Math.round(opt.lacunarity !== undefined ? opt.lacunarity : 2));
  const seed = opt.seed !== undefined ? opt.seed : 0;
  const useGrad = opt.gradient === true;
  let fx = freqX;
  let fy = freqY;
  let amp = 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const s = (seed + o * 0x9e37 + 0x1b3) | 0;
    const n = useGrad
      ? unsignedOf(gradientNoise2(u * fx, v * fy, s, fx, fy))
      : valueNoise2(u * fx, v * fy, s, fx, fy);
    sum += n * amp;
    norm += amp;
    amp *= gain;
    fx *= lac;
    fy *= lac;
  }
  return norm > 0 ? clamp01(sum / norm) : 0;
}

/**
 * Ridged fractal noise in `[0,1]`. Sharp crests, smooth valleys — the right primitive for
 * chipped concrete edges, cast-metal ridges and mountain-like grime crusts.
 * `sharpness` raises the ridge exponent; 1 is a plain absolute-value ridge.
 */
export function tileRidged2(u, v, opt) {
  const freqX = freqOf(opt.freqX !== undefined ? opt.freqX : 4);
  const freqY = freqOf(opt.freqY !== undefined ? opt.freqY : freqX);
  const octaves = opt.octaves !== undefined ? opt.octaves : 4;
  const gain = opt.gain !== undefined ? opt.gain : 0.5;
  const lac = Math.max(2, Math.round(opt.lacunarity !== undefined ? opt.lacunarity : 2));
  const seed = opt.seed !== undefined ? opt.seed : 0;
  const sharpness = opt.sharpness !== undefined ? opt.sharpness : 1;
  let fx = freqX;
  let fy = freqY;
  let amp = 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const s = (seed + o * 0x85eb + 0x2f1) | 0;
    let r = 1 - Math.abs(gradientNoise2(u * fx, v * fy, s, fx, fy));
    if (sharpness !== 1) r = Math.pow(r, sharpness);
    sum += r * amp;
    norm += amp;
    amp *= gain;
    fx *= lac;
    fy *= lac;
  }
  return norm > 0 ? clamp01(sum / norm) : 0;
}

/** Billowed fractal noise in `[0,1]` — puffy lobes. Reads as aggregate, foam and rust blooms. */
export function tileBillow2(u, v, opt) {
  const freqX = freqOf(opt.freqX !== undefined ? opt.freqX : 4);
  const freqY = freqOf(opt.freqY !== undefined ? opt.freqY : freqX);
  const octaves = opt.octaves !== undefined ? opt.octaves : 4;
  const gain = opt.gain !== undefined ? opt.gain : 0.5;
  const lac = Math.max(2, Math.round(opt.lacunarity !== undefined ? opt.lacunarity : 2));
  const seed = opt.seed !== undefined ? opt.seed : 0;
  let fx = freqX;
  let fy = freqY;
  let amp = 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const s = (seed + o * 0x27d4 + 0x5b7) | 0;
    sum += Math.abs(gradientNoise2(u * fx, v * fy, s, fx, fy)) * amp;
    norm += amp;
    amp *= gain;
    fx *= lac;
    fy *= lac;
  }
  return norm > 0 ? clamp01(sum / norm) : 0;
}

/** Turbulence: fbm of absolute gradient noise, then contrast-shaped. Streaky grime deposits. */
export function tileTurbulence2(u, v, opt) {
  const t = tileBillow2(u, v, opt);
  const contrast = opt.contrast !== undefined ? opt.contrast : 1;
  return contrast === 1 ? t : clamp01(Math.pow(t, contrast));
}

/** Result record for {@link tileWarp2}. */
export function createWarpResult() {
  return { u: 0, v: 0 };
}

/**
 * Seamless domain warp. Offsets `(u,v)` by a periodic vector field, so any `tile*` function
 * sampled at the warped coordinates is still seamless.
 *
 * @param {number} amp offset amplitude in tile units
 * @param {object} out record from {@link createWarpResult}
 */
export function tileWarp2(u, v, freq, amp, seed = 0, out = createWarpResult()) {
  const f = freqOf(freq);
  const nx = gradientNoise2(u * f, v * f, (seed ^ 0x1a2b3c) | 0, f, f);
  const ny = gradientNoise2(u * f, v * f, (seed ^ 0x4d5e6f) | 0, f, f);
  out.u = u + nx * amp;
  out.v = v + ny * amp;
  return out;
}

const WARP_SCRATCH = createWarpResult();

/** Seamless Worley over the unit tile. `freq` = cells across the tile. */
export function tileWorley2(u, v, freq, seed = 0, out = createCellResult()) {
  const f = freqOf(freq);
  return worley2(u * f, v * f, seed, f, out);
}

/**
 * Directional streak noise — the primitive behind brushed metal, hair strands, rain sheets and
 * wood grain. Frequency is high *across* the streaks and low *along* them, and only the across
 * frequency multiplies per octave, so streaks stay long while gaining fine detail.
 *
 * `shear` must be an integer: it is the number of tile-heights the streaks travel per
 * tile-width. Any non-integer shear would break tiling, so the value is rounded.
 *
 * @param {object} opt
 * @param {number} opt.across repeats perpendicular to the streaks
 * @param {number} opt.along repeats along the streaks (2 or more for any variation)
 * @param {number} [opt.octaves]
 * @param {number} [opt.gain]
 * @param {number} [opt.shear] integer tilt
 * @param {boolean} [opt.horizontal] streaks run along u instead of v
 * @param {number} [opt.seed]
 * @returns {number} `[0,1]`
 */
export function tileStreak2(u, v, opt) {
  const horizontal = opt.horizontal === true;
  // `a` runs across the streaks, `b` runs along them.
  const a = horizontal ? v : u;
  const b = horizontal ? u : v;
  const shear = Math.round(opt.shear !== undefined ? opt.shear : 0);
  const along = freqOf(opt.along !== undefined ? opt.along : 2);
  const octaves = opt.octaves !== undefined ? opt.octaves : 3;
  const gain = opt.gain !== undefined ? opt.gain : 0.55;
  const seed = opt.seed !== undefined ? opt.seed : 0;
  const bSheared = b + a * shear;
  let across = freqOf(opt.across !== undefined ? opt.across : 64);
  let amp = 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const s = (seed + o * 0x165f + 0x77) | 0;
    sum += valueNoise2(a * across, bSheared * along, s, across, along) * amp;
    norm += amp;
    amp *= gain;
    across *= 2;
  }
  return norm > 0 ? clamp01(sum / norm) : 0;
}

/**
 * Crack / fracture ridge in `[0,1]`, 1 exactly on a Voronoi boundary. `width` is the crack
 * half-width in cell units; `warp` bends the cell walls so cracks meander instead of forming
 * a mesh of straight lines, which is the difference between "fractured" and "computer graphics".
 *
 * @param {object} opt
 * @param {number} opt.freq cells across the tile
 * @param {number} [opt.width] boundary width in cell units
 * @param {number} [opt.warp] domain-warp amplitude in tile units
 * @param {number} [opt.warpFreq]
 * @param {number} [opt.seed]
 */
export function tileCrack2(u, v, opt) {
  const width = opt.width !== undefined ? opt.width : 0.06;
  const warp = opt.warp !== undefined ? opt.warp : 0.03;
  let su = u;
  let sv = v;
  if (warp > 0) {
    tileWarp2(u, v, opt.warpFreq !== undefined ? opt.warpFreq : 6, warp, (opt.seed ^ 0x3f17) | 0, WARP_SCRATCH);
    su = WARP_SCRATCH.u;
    sv = WARP_SCRATCH.v;
  }
  tileWorley2(su, sv, opt.freq !== undefined ? opt.freq : 8, opt.seed !== undefined ? opt.seed : 0, CELL_SCRATCH);
  const edge = CELL_SCRATCH.f2 - CELL_SCRATCH.f1;
  return 1 - smoothstep(0, width, edge);
}

/**
 * Multi-scale fracture field in `[0,1]`: a coarse crack network with finer branch cracks
 * layered on top by `max`, so branches terminate on trunks instead of crossing them.
 *
 * @param {object} opt
 * @param {number} opt.freq base cells across the tile
 * @param {number} [opt.octaves] number of crack scales
 * @param {number} [opt.width]
 * @param {number} [opt.gain] amplitude falloff per scale
 * @param {number} [opt.warp]
 * @param {number} [opt.seed]
 */
export function tileFracture2(u, v, opt) {
  const octaves = opt.octaves !== undefined ? opt.octaves : 3;
  const gain = opt.gain !== undefined ? opt.gain : 0.6;
  const seed = opt.seed !== undefined ? opt.seed : 0;
  const warp = opt.warp !== undefined ? opt.warp : 0.03;
  let freq = opt.freq !== undefined ? opt.freq : 6;
  let width = opt.width !== undefined ? opt.width : 0.07;
  let amp = 1;
  let best = 0;
  for (let o = 0; o < octaves; o++) {
    const c = tileCrack2(u, v, {
      freq,
      width,
      warp: warp / (o + 1),
      warpFreq: freq,
      seed: (seed + o * 0x5bd1) | 0,
    });
    const contribution = c * amp;
    if (contribution > best) best = contribution;
    amp *= gain;
    freq *= 2;
    width *= 0.85;
  }
  return clamp01(best);
}

// ---------------------------------------------------------------------------
// Structured tiling patterns — grids, panels, brickwork, treadplate
// ---------------------------------------------------------------------------

/** Result record for {@link gridCell2} and {@link brickCell2}. */
export function createGridResult() {
  return { cellU: 0, cellV: 0, ix: 0, iy: 0, id: 0, value: 0, edge: 0, edgeU: 0, edgeV: 0 };
}

/**
 * Regular grid decomposition. `edge` is the normalised distance to the nearest cell border
 * (0 on the border, 1 at the cell centre) — feed it to `smoothstep` for mortar, panel gaps
 * and plank seams.
 */
export function gridCell2(u, v, cols, rows, seed = 0, out = createGridResult()) {
  const c = Math.max(1, Math.round(cols));
  const r = Math.max(1, Math.round(rows));
  const fu = u * c;
  const fv = v * r;
  const ix = wrapi(Math.floor(fu), c);
  const iy = wrapi(Math.floor(fv), r);
  const cu = fract(fu);
  const cv = fract(fv);
  out.cellU = cu;
  out.cellV = cv;
  out.ix = ix;
  out.iy = iy;
  out.id = ihash2(ix, iy, seed);
  out.value = out.id / 4294967296;
  out.edgeU = Math.min(cu, 1 - cu) * 2;
  out.edgeV = Math.min(cv, 1 - cv) * 2;
  out.edge = Math.min(out.edgeU, out.edgeV);
  return out;
}

/**
 * Running-bond grid: each row is offset along u. `offset` is the fraction of a cell width per
 * row; 0.5 gives classic brickwork, 0.33 gives a plank floor that reads as laid, not printed.
 * Seamless as long as `offset * rows` is an integer, which the function enforces by rounding.
 */
export function brickCell2(u, v, cols, rows, offset = 0.5, seed = 0, out = createGridResult()) {
  const c = Math.max(1, Math.round(cols));
  const r = Math.max(1, Math.round(rows));
  // Quantise the per-row offset so that after `rows` rows the pattern has shifted a whole
  // number of cells — otherwise the top and bottom tile edges disagree.
  const steps = Math.max(1, Math.round(offset * r));
  const off = steps / r;
  const fv = v * r;
  const iy = wrapi(Math.floor(fv), r);
  const fu = (u + iy * off) * c;
  const ix = wrapi(Math.floor(fu), c);
  const cu = fract(fu);
  const cv = fract(fv);
  out.cellU = cu;
  out.cellV = cv;
  out.ix = ix;
  out.iy = iy;
  out.id = ihash2(ix, iy, seed);
  out.value = out.id / 4294967296;
  out.edgeU = Math.min(cu, 1 - cu) * 2;
  out.edgeV = Math.min(cv, 1 - cv) * 2;
  out.edge = Math.min(out.edgeU, out.edgeV);
  return out;
}

/**
 * Diamond treadplate profile in `[0,1]`: raised lozenges in a staggered lattice, the stamped
 * anti-slip pattern on industrial steel. Seamless for integer `cols`/`rows`.
 */
export function treadDiamond2(u, v, cols, rows, sharpness = 2.2) {
  const c = Math.max(1, Math.round(cols));
  const r = Math.max(1, Math.round(rows));
  // Two rotated square lattices, offset, give the alternating-lozenge look.
  const a = fract(u * c + v * r) * 2 - 1;
  const b = fract(u * c - v * r) * 2 - 1;
  const d = Math.max(Math.abs(a), Math.abs(b));
  return clamp01(Math.pow(1 - d, sharpness));
}

/**
 * Hexagonal cell field. `out.edge` is distance to the nearest hex boundary in cell units.
 * Used for wire mesh and safety glass. Seamless when `cols` is even.
 */
export function hexCell2(u, v, cols, seed = 0, out = createGridResult()) {
  const c = Math.max(2, Math.round(cols / 2) * 2);
  const rowH = 0.8660254; // sqrt(3)/2 — hex rows are shorter than they are wide
  const rows = Math.max(2, Math.round(c / rowH / 2) * 2);
  return brickCell2(u, v, c, rows, 0.5, seed, out);
}

// ---------------------------------------------------------------------------
// Triplanar helpers — for geometry the bakers cannot give clean UVs
// ---------------------------------------------------------------------------

/** Result record for {@link triplanarWeights}. */
export function createTriplanarWeights() {
  return { x: 0, y: 0, z: 0 };
}

/**
 * Normalised triplanar blend weights from a surface normal. `sharpness` above 1 narrows the
 * blend bands so the three projections do not smear into each other.
 */
export function triplanarWeights(nx, ny, nz, sharpness = 4, out = createTriplanarWeights()) {
  let wx = Math.pow(Math.abs(nx), sharpness);
  let wy = Math.pow(Math.abs(ny), sharpness);
  let wz = Math.pow(Math.abs(nz), sharpness);
  const sum = wx + wy + wz;
  if (sum <= 0) {
    out.x = 0;
    out.y = 1;
    out.z = 0;
    return out;
  }
  const inv = 1 / sum;
  wx *= inv;
  wy *= inv;
  wz *= inv;
  out.x = wx;
  out.y = wy;
  out.z = wz;
  return out;
}

const TRI_SCRATCH = createTriplanarWeights();

/**
 * Samples a 2D field on the three world planes and blends by normal. `fn(a, b)` receives
 * coordinates already multiplied by `scale`, so it can be any `tile*` closure.
 *
 * @param {(a:number, b:number) => number} fn
 */
export function triplanarSample(fn, x, y, z, nx, ny, nz, scale = 1, sharpness = 4) {
  triplanarWeights(nx, ny, nz, sharpness, TRI_SCRATCH);
  let acc = 0;
  if (TRI_SCRATCH.x > 0) acc += fn(z * scale, y * scale) * TRI_SCRATCH.x;
  if (TRI_SCRATCH.y > 0) acc += fn(x * scale, z * scale) * TRI_SCRATCH.y;
  if (TRI_SCRATCH.z > 0) acc += fn(x * scale, y * scale) * TRI_SCRATCH.z;
  return acc;
}

// ---------------------------------------------------------------------------
// Shaping utilities used heavily by the recipes in library.js
// ---------------------------------------------------------------------------

/** S-curve contrast around a pivot. `amount` 0 = unchanged, 1 = hard. */
export function contrast01(v, amount, pivot = 0.5) {
  if (amount <= 0) return clamp01(v);
  const t = clamp01(v);
  const k = 1 + amount * 7;
  const x = t < pivot ? (pivot === 0 ? 0 : t / pivot) : (pivot === 1 ? 1 : (t - pivot) / (1 - pivot));
  const shaped = Math.pow(x, k / (k * 0.5 + 0.5));
  const s = shaped * shaped * (3 - 2 * shaped);
  return clamp01(t < pivot ? s * pivot : pivot + s * (1 - pivot));
}

/** Remaps `[inLo,inHi]` onto `[0,1]` with clamping — the levels tool for noise fields. */
export function levels01(v, inLo, inHi) {
  if (inHi === inLo) return v < inLo ? 0 : 1;
  return clamp01((v - inLo) / (inHi - inLo));
}

/** Soft maximum — blends two masks without a hard crease. `k` is the blend radius. */
export function softMax(a, b, k = 0.1) {
  if (k <= 0) return Math.max(a, b);
  const h = clamp01(0.5 + (b - a) / (2 * k));
  return mix(a, b, h) + k * h * (1 - h);
}

/** Soft minimum, the counterpart to {@link softMax}. */
export function softMin(a, b, k = 0.1) {
  return -softMax(-a, -b, k);
}
